import React, { useEffect, useMemo, useState } from 'react';
import { hasDashboardFullAccess } from '../utils/workspaces';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MdDashboard, 
  MdPeople, 
  MdCheckCircle, 
  MdNoteAlt, 
  MdMenuBook, 
  MdSchool, 
  MdGroup, 
  MdCalendarToday, 
  MdSettings,
  MdExpandMore,
  MdAssessment,
  MdNotifications,
  MdVerifiedUser,
} from 'react-icons/md';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const MOBILE_BREAKPOINT = 767;

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb; /* gray-200 */
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '64px')};

  /* Below the breakpoint the sidebar is a full-width drawer that slides in
     over the content instead of a column that shrinks to an icon rail. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 256px;
    border-right: none;
    transition: transform 0.25s ease;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    box-shadow: ${(p) => (p.$open ? '4px 0 24px rgba(0,0,0,0.18)' : 'none')};
  }
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;
  position: relative;
`;

const CloseButton = styled.button`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: #6b7280;
    padding: 6px;
    border-radius: 8px;
    cursor: pointer;
    &:hover { background: #f3f4f6; color: #111827; }
  }
`;

const BrandBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BrandBadge = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #4f46e5; /* indigo-600 */
  color: #fff;
  font-weight: 700;
  font-size: 12px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 12px 8px;
  overflow-y: auto;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: ${(p) => (p.$active ? '#4f46e5' : 'transparent')};
  color: ${(p) => (p.$active ? '#ffffff' : '#374151')};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  &:hover { background: ${(p) => (p.$active ? '#4338ca' : '#f3f4f6')}; }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: ${(p) => (p.$isChild ? '8px' : '12px')};
  padding: ${(p) => (p.$isChild ? '8px 12px' : '10px 12px')};
  color: ${(p) => {
    if (p.$isChild && p.$active) return '#4f46e5';
    if (p.$active) return '#fff';
    return '#374151';
  }};
  background: ${(p) => {
    if (p.$isChild && p.$active) return '#eef2ff';
    if (p.$active) return '#4f46e5';
    return 'transparent';
  }};
  border-left: ${(p) => (p.$isChild && p.$active ? '3px solid #4f46e5' : '3px solid transparent')};
  text-decoration: none;
  font-size: ${(p) => (p.$isChild ? '13px' : '14px')};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  transition: all 0.2s;
  
  &:hover {
    background: ${(p) => {
      if (p.$isChild && p.$active) return '#e0e7ff';
      if (p.$active) return '#4338ca';
      if (p.$isChild) return '#f9fafb';
      return '#f3f4f6';
    }};
    color: ${(p) => (p.$isChild && !p.$active ? '#4f46e5' : p.$active && !p.$isChild ? '#fff' : '#374151')};
  }
  
  svg {
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const ChildWrap = styled.div`
  margin-top: 6px;
  padding-left: 12px;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const Footer = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 16px;
  color: #6b7280; /* gray-500 */
  font-size: 12px;
`;

// Icon mapping
const getIcon = (name) => {
  const iconMap = {
    dashboard: <MdDashboard />,
    students: <MdPeople />,
    correction: <MdCheckCircle />,
    notepad: <MdNoteAlt />,
    report: <MdAssessment />,
    notifications: <MdNotifications />,
    courses: <MdMenuBook />,
    academic: <MdSchool />,
    staff: <MdGroup />,
    schedule: <MdCalendarToday />,
    settings: <MdSettings />,
    profileRequests: <MdVerifiedUser />,
  };
  return iconMap[name] || <MdDashboard />;
};

export default function Sidebar({ isOpen, onToggle, onNavigate }) {
  const [expanded, setExpanded] = useState([]);

  const user = AuthService.getUser();
  const userRole = user?.superAdmin?.staff?.role || user?.superAdmin?.role;
  const userType = user?.superAdmin ? 'super_admin' : user?.type;
  const isSuperAdmin = hasDashboardFullAccess(user);
  const isAdmin = isSuperAdmin || userRole === 'Admin' || userRole === 'Administrator';
  const staffTypeNormalized = String(user?.superAdmin?.staff?.staffType || '').trim().toLowerCase();
  const isManagement = staffTypeNormalized === 'management' || hasDashboardFullAccess(user);

  // Show Notepad section for all logged-in admin-frontend users
  const showNotepad = Boolean(user);
  const [hasHndCourses, setHasHndCourses] = useState(false);
  const [hasTopupCourses, setHasTopupCourses] = useState(false);
  const [hasDiplomaCourses, setHasDiplomaCourses] = useState(false);
  const [canAccessBatchGroups, setCanAccessBatchGroups] = useState(false);
  const [hasCoordinateCourses, setHasCoordinateCourses] = useState(false);
  const [studentIssueOpenCount, setStudentIssueOpenCount] = useState(0);
  const [hodUnreadCount, setHodUnreadCount] = useState(0);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    let cancelled = false;

    const loadAccessFlags = async () => {
      try {
        const resp = await axiosInstance.get('/admin/me/coordinate-courses');
        if (cancelled || !resp?.data) return;
        const data = resp.data;
        setHasHndCourses(Boolean(data.hasHndCourses));
        setHasTopupCourses(Boolean(data.hasTopupCourses));
        setHasDiplomaCourses(Boolean(data.isSuperAdmin) || Boolean(data.hasDiplomaCourses));

        const coordinateCourses = data.coordinateCourses || {};

        // Mirror the HND (IT) detection used by the Delivery backend.
        const HND_NAMES = [
          'Level 5 Pearson BTEC HND in Computing (General)',
          'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
          'Level 5 Pearson BTEC HND in Computing (Network Engineering)',
          'BTEC HND in Digital Technologies',
        ].map((v) => v.toLowerCase());

        const HND_LIKE_GROUPS = [
          ['hnd', 'computing'],
          ['hnd', 'digital technologies'],
        ];

        const coordinateCourseList = Object.values(coordinateCourses || {});

        const hasHndItCourse = coordinateCourseList.some((course) => {
          if (!course) return false;
          const fullNameLower = String(course.fullName || course.courseName || '').toLowerCase().trim();
          const shortNameLower = String(course.shortName || course.courseShortName || '').toLowerCase().trim();

          // First, try exact / contains match against configured HND Computing names
          const nameMatch = HND_NAMES.some((target) => {
            if (!target) return false;
            return (
              fullNameLower === target ||
              fullNameLower.includes(target) ||
              shortNameLower === target ||
              shortNameLower.includes(target)
            );
          });
          if (nameMatch) return true;

          // Fallback LIKE-style groups: all tokens must appear in either full or short name
          const fields = [fullNameLower, shortNameLower];
          const likeMatch = HND_LIKE_GROUPS.some((tokens) =>
            tokens.every((token) => {
              const lower = token.toLowerCase();
              return fields.some((field) => field.includes(lower));
            })
          );

          return likeMatch;
        });

        setCanAccessBatchGroups(Boolean(data.isSuperAdmin) || hasHndItCourse);

        const accessibleIds = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
        setHasCoordinateCourses(coordinateCourseList.length > 0 || accessibleIds.length > 0);
      } catch (err) {
        if (!cancelled) {
          setHasHndCourses(false);
          setHasTopupCourses(false);
          setHasDiplomaCourses(false);
          setCanAccessBatchGroups(false);
          setHasCoordinateCourses(false);
        }
      }
    };

    loadAccessFlags();

    return () => {
      cancelled = true;
    };
  }, []);

  // Poll the live "open" ticket count for super admins -- same
  // status-counts endpoint that feeds the "Open" tab badge on the Student
  // Issues page itself, so the two numbers always agree.
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOpenCount = () => {
      axiosInstance.get('/contact-admin/issues/status-counts')
        .then((r) => setStudentIssueOpenCount(r?.data?.open || 0))
        .catch(() => {});
    };
    fetchOpenCount();
    const timer = setInterval(fetchOpenCount, 2 * 60_000);
    // Refresh immediately on the same actions that already move issues
    // in/out of "open" from the Student Issues page: a status change, or a
    // conversation being read (covers issues auto-resolved on reply, etc).
    window.addEventListener('ka-student-issue-status-changed', fetchOpenCount);
    window.addEventListener('ka-student-issue-read', fetchOpenCount);
    return () => {
      clearInterval(timer);
      window.removeEventListener('ka-student-issue-status-changed', fetchOpenCount);
      window.removeEventListener('ka-student-issue-read', fetchOpenCount);
    };
  }, [isSuperAdmin]);

  // Unread lecturer messages for the HOD inbox (super admin + management only).
  useEffect(() => {
    if (!isSuperAdmin && !isManagement) return undefined;
    let cancelled = false;
    const fetchUnread = () => {
      axiosInstance.get('/admin/hod-notifications/summary')
        .then((r) => { if (!cancelled) setHodUnreadCount(r?.data?.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 60_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isSuperAdmin, isManagement]);

  const navigation = useMemo(() => ([
    { name: 'Dashboard', href: '/admin/dashboard', icon: getIcon('dashboard') },
    {
      name: 'Students', href: '#', icon: getIcon('students'),
      children: [
        { name: 'All Students', href: '/admin/students' },
        { name: 'Register Pool', href: '/admin/register-pool' },
        { name: 'My Course Due', href: '/admin/my-course-due' },
        { name: 'Inactivated Inquiry', href: '/admin/inactivated-inquiry' },
        { name: 'Inactivated List Followup', href: '/admin/inactivated-inquiry/by-assignee' },
        ...(isAdmin ? [{ name: 'Students Without Registration', href: '/admin/students-without-registration' }] : []),
        { name: 'Profile Edit Requests', href: '/admin/student-profile-edit-requests' },
      ],
    },
    ...(isSuperAdmin ? [{ name: 'Correction', href: '/admin/correction', icon: getIcon('correction') }] : []),
    ...(showNotepad ? [{ name: 'Notepad', href: '#', icon: getIcon('notepad'), children: [
      { name: 'To Do List', href: '/admin/todo-list' },
      { name: 'Student Issue', href: '/admin/student-tickets' },
      // Reminder + Sticky Note: the real super-admin account only.
      ...(user?.superAdmin?.superAdminId ? [
        { name: 'Remider', href: '/admin/remider' },
        { name: 'Sticky Note', href: '/admin/sticky-note' },
      ] : []),
      ...(isAdmin ? [{ name: 'Contacts', href: '/admin/contacts' }] : []),
      ...(isAdmin ? [{ name: 'Problems', href: '/admin/problems' }] : []),
      ...(user?.superAdmin?.superAdminId ? [{ name: 'Rules & Procedures', href: '/admin/rule-notes' }] : []),
      { name: 'Issue Tracker', href: '/admin/issue-tracker' },
    ]}] : []),
    ...(isSuperAdmin ? [{
      name: 'Student Issues',
      href: '/admin/student-issue',
      badge: studentIssueOpenCount,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      ),
    }] : []),
    ...(isSuperAdmin || isManagement ? [{
      name: 'Lecturer Messages',
      href: '/admin/hod-notifications',
      badge: hodUnreadCount,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16a2 2 0 012 2v9a2 2 0 01-2 2H8l-4 4V6a2 2 0 012-2z" />
        </svg>
      ),
    }] : []),
    {
      name: 'Lab Jobs',
      href: '/admin/lab-jobs',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 3h6M10 3v6.5L4.6 18.2A2 2 0 006.3 21h11.4a2 2 0 001.7-2.8L14 9.5V3" />
          <path d="M7.5 15h9" />
        </svg>
      ),
    },
    { name: 'Courses', href: '#', icon: getIcon('courses'), children: [
      { name: 'All Courses', href: '/admin/courses' },
    ]},
    { name: 'Academic', href: '#', icon: getIcon('academic'), children: [
      { name: 'Delivery', href: '/admin/delivery' },
      { name: 'My Exam', href: '/admin/my-exam' },
      { name: 'Academic Calendar', href: '/admin/academic-calendar' },
      ...(hasHndCourses ? [{ name: 'HND Exam Board', href: '/admin/hnd-exam-board' }] : []),
      ...(hasTopupCourses ? [{ name: 'Topup Exam Board', href: '/admin/topup-exam-board' }] : []),
      { name: 'Not Corrected Assignment List', href: '/admin/not-corrected-assignment-list' },
      ...(hasHndCourses ? [{ name: 'HND Assignment Tracking', href: '/admin/hnd-assignment-tracking' }] : []),
      ...(hasTopupCourses ? [{ name: 'TOPUP Assignment Tracking', href: '/admin/topup-assignment-tracking' }] : []),
      ...(hasDiplomaCourses ? [{ name: 'Diploma Results', href: '/admin/diploma-batch-phases' }] : []),
    ]},
    { name: 'Staff', href: '#', icon: getIcon('staff'), children: [
      { name: 'All Staff', href: '/admin/staff' },
      ...(isAdmin ? [{ name: 'Due Course Mapping', href: '/admin/due-course-mapping' }] : []),
      ...(isSuperAdmin ? [{ name: 'Course Mapping', href: '/admin/course-mapping' }] : []),
      ...(isAdmin ? [{ name: 'Staff Unavailability', href: '/admin/staff-unavailability' }] : []),
    ]},
    { name: 'Schedule', href: '#', icon: getIcon('schedule'), children: [
      { name: 'Timetable', href: '/admin/schedule' },
      ...(isAdmin ? [{ name: 'Other Event Types', href: '/admin/other-event-types' }] : []),
      { name: 'Attendance Tracking', href: '/admin/attendance-tracking' },
      ...(isAdmin ? [{ name: 'Lecture Hours', href: '/admin/lecture-hours' }] : []),
      ...(isAdmin ? [{ name: 'Lecturer Subjects', href: '/admin/lecturer-subjects' }] : []),
      { name: 'Students Attendance', href: '/admin/students-attendance' },
      ...(isAdmin ? [{ name: 'Average Attendance', href: '/admin/average-attendance-staff' }] : []),
    ]},
    ...(user?.superAdmin?.superAdminId ? [{
      name: 'Study Materials',
      href: '/admin/study-materials',
      icon: getIcon('courses'),
    }] : []),
    ...(isSuperAdmin ? [{
      name: 'Weekly Review',
      href: '#',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
      children: [
        { name: 'Staff Incidents', href: '/admin/incidents' },
        { name: 'Meetings', href: '/admin/meetings' },
      ],
    }] : []),
    ...(isAdmin || isManagement || hasCoordinateCourses ? [{
      name: 'Report',
      href: '#',
      icon: getIcon('report'),
      children: [
        ...(isAdmin ? [{ name: 'Student Detail', href: '/admin/report/student-detail' }] : []),
        ...(isAdmin || hasCoordinateCourses ? [{ name: 'Coordinator Reports', href: '/admin/coordinator-reports' }] : []),
        ...(isSuperAdmin || isManagement ? [{ name: 'Monitor Report', href: '/admin/report/monitor' }] : []),
      ],
    }] : []),
    ...(isAdmin ? [{
      name: 'Notifications',
      href: '/admin/notifications',
      icon: getIcon('notifications'),
    }] : []),
    ...(() => {
      const settingsChildren = [];

      if (isSuperAdmin) {
        settingsChildren.push(
          { name: 'Positions', href: '/admin/positions' },
          { name: 'Courses', href: '/admin/courses' },
          { name: 'Subjects', href: '/admin/subjects' },
          { name: 'Assign Subject', href: '/admin/assign-subject' },
          { name: 'Classroom', href: '/admin/classroom' },
          { name: 'Batch', href: '/admin/batches' },
          { name: 'Batch Groups', href: '/admin/batch-groups' },
          { name: 'ELMS Module Code', href: '/admin/elms-module-code' },
          { name: 'Ticket Categories', href: '/admin/ticket-categories' },
          ...(user?.superAdmin?.superAdminId ? [{ name: 'Database Activity', href: '/admin/data-tools' }] : []),
          ...(isAdmin ? [
            { name: 'Group Diploma Courses', href: '/admin/group-diploma-courses' },
          ] : []),
        );
      } else if (canAccessBatchGroups) {
        // Non-superadmin coordinators with HND-IT access: only Batch + Batch Groups
        settingsChildren.push(
          { name: 'Batch', href: '/admin/batches' },
          { name: 'Batch Groups', href: '/admin/batch-groups' },
        );
      } else {
        // Everyone else keeps only the Batch option
        settingsChildren.push({ name: 'Batch', href: '/admin/batches' });
      }

      if (!settingsChildren.length) {
        return [];
      }

      return [{ name: 'Settings', href: '#', icon: getIcon('settings'), children: settingsChildren }];
    })(),
  ]), [isAdmin, isSuperAdmin, isManagement, showNotepad, hasHndCourses, hasTopupCourses, hasDiplomaCourses, canAccessBatchGroups, hasCoordinateCourses, studentIssueOpenCount, hodUnreadCount]);

  useEffect(() => {
    const activeParents = [];
    navigation.forEach((item) => {
      if (item.children && item.children.some((child) => isActive(child.href))) {
        activeParents.push(item.name);
      }
    });
    if (activeParents.length) {
      setExpanded((prev) => Array.from(new Set([...prev, ...activeParents])));
    }
  }, [currentPath, navigation]);

  const isActive = (href) => {
    if (href === '/admin/dashboard') return currentPath === href;
    return currentPath.startsWith(href) && href !== '#';
  };

  const toggleExpanded = (name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  return (
    <Aside $open={isOpen}>
      <TopBar>
        {isOpen ? (
          <BrandBox>
            <BrandBadge>KA</BrandBadge>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Kaaryaalaya</span>
          </BrandBox>
        ) : (
          <BrandBadge>KA</BrandBadge>
        )}
        <CloseButton onClick={onToggle} aria-label="Close menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </CloseButton>
      </TopBar>
      <Nav>
        {navigation.map((item) => {
          const hasChildren = !!(item.children && item.children.length);
          const itemActive = isActive(item.href) || (item.children && item.children.some((c) => isActive(c.href)));
          if (hasChildren) {
            const isExpanded = expanded.includes(item.name);
            return (
              <div key={item.name} style={{ marginBottom: 6 }}>
                <NavButton onClick={() => toggleExpanded(item.name)} $active={itemActive}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{item.icon}</span>
                    {isOpen && <span>{item.name}</span>}
                  </span>
                  {isOpen && (
                    <MdExpandMore style={{ fontSize: 18, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  )}
                </NavButton>
                {isOpen && (
                  <ChildWrap $open={isExpanded}>
                    {item.children.map((child) => {
                      const childActive = isActive(child.href);
                      return (
                        <div key={child.name} style={{ marginBottom: 4 }}>
                          <NavLink to={child.href} $active={childActive} $isChild={true} onClick={onNavigate}>
                            <span>{child.name}</span>
                          </NavLink>
                        </div>
                      );
                    })}
                  </ChildWrap>
                )}
              </div>
            );
          }
          return (
            <div key={item.name} style={{ marginBottom: 8, position: 'relative' }}>
              <NavLink to={item.href} $active={itemActive} onClick={onNavigate}>
                <span style={{ position: 'relative', flexShrink: 0 }}>
                  {item.icon}
                  {!isOpen && item.badge > 0 && (
                    <span style={{
                      position: 'absolute', top: -4, right: -4,
                      minWidth: 16, height: 16, borderRadius: 9999,
                      background: '#ef4444', color: '#fff',
                      fontSize: 9, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', lineHeight: 1,
                    }}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </span>
                {isOpen && <span style={{ flex: 1 }}>{item.name}</span>}
                {isOpen && item.badge > 0 && (
                  <span style={{
                    minWidth: 20, height: 20, borderRadius: 9999,
                    background: '#ef4444', color: '#fff',
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 5px', lineHeight: 1, flexShrink: 0,
                  }}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 600 }}>Kaaryaalaya Analysis</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
